import { createReducer, on } from '@ngrx/store';
import * as JournalActions from '../_actions/journal.actions';
import * as TemplateActions from '../_actions/template.actions';
import { JasminValidationStatus } from '../_interface/jasmin-generation.interface';
import { IEntryForShow } from '../_interface/ientry-for-show';
import { DocumentationEntryForShowDto } from 'app/_models/model/documentationEntryForShowDto';
import { DocumentationTemplateFieldDto } from 'app/_models/model/documentationTemplateFieldDto';
import { JournalDisplayView } from '../_interface/journal-display-view.enum';
import { JournalConfig } from '../lib/journal.config';
import { DocumentationTemplateDto } from 'app/_models/model/documentationTemplateDto';
import { enhanceWithEntryType } from './enhanceWithEntryType.fn';
import { DocumentationCitizenDto } from 'app/_models/model/documentationCitizenDto';
import { ListSortEnum } from '../_interface/list-sort.enum';
import { environment } from '../../../../src/environments/environment';
import { EnvsEnum } from '../../../../src/environments/environment.interface';
import { JournalEditMode } from '../_interface/journal-edit-mode.enum';
import { IEditableTemplateFields } from '../_interface/editable-template-fields';
import { ILayout } from '../_interface/layout';
import {
	addFieldInLayout,
	addFieldInLayoutAtStartOrEnd,
	removeFieldInLayoutAt,
	vectorMoveField,
	vectorMoveFieldTo
} from './vectorMoveField.fn';
import { addFieldsToLayout } from './addNewFieldsToLayout.fn';
import { fillLayout, restoreFields, restoreRow } from './restoreLayout.fn';
import { getPositionInLayoutAsObject } from './fieldInLayoutPos.fn';
import { isComplexOrWidgetFn } from './isWhatType.fn';
import { SettingRow } from 'app/_interfaces/settings.interface';
import { Dictionary } from '@ngrx/entity/src/models';
import { tryParseJson } from 'shared/_functions/tryParseJson';
import { JournalSettingsKeys } from '../../../settings/src/lib/journal-settings/journal-settings.defaults2';
import { isEmpty } from 'lodash-es';
import {parseBoolean} from 'shared/_functions/parseBoolean';
import {getDefaultValue, getSettingValue} from 'shared/_functions/getSettingValue';

export const journalFeatureKey = 'journal';

export interface JournalState {
	init: boolean;
	config: JournalConfig;
	complexFieldsEnabled: boolean;
	rawEntries: DocumentationEntryForShowDto[];
	rawTemplateFields: DocumentationTemplateFieldDto[];
	rawUserTemplates: DocumentationTemplateDto[];
	medicineEntries: IEntryForShow[];
	newGuid: string;
	display: JournalDisplayView;
	citizenId: string;
	citizen: DocumentationCitizenDto;
	editEntrySaved: {
		saving: boolean;
		entry: DocumentationEntryForShowDto;
		entryId: string;
		backToList: boolean
	};
	editTemplate: {
		template: DocumentationTemplateDto;
		fields: IEditableTemplateFields;
		layout: ILayout;
		openForEdit: string;
		lastChangedField: string[];
		mode: JournalEditMode;
		valid: boolean;
		fieldsToSave: DocumentationTemplateFieldDto[];
		templateToSave: DocumentationTemplateDto;
		saved: boolean;
		saving: boolean;
		layoutVisible: boolean,
		goBackAfterSave: boolean,
		clinicProfessions: number[]
	};
	byList: {
		sortBy: ListSortEnum;
		direction: -1 | 1,
		printMode: boolean,
		entriesPerPage: number
	};
	byDrawings: {
		descriptions: boolean;
		bigSize: boolean;
		showInactiveSymbols: boolean,
		hiddenSchemaIds: string[]
	};
	drawingsColumnVisible: boolean;
	entriesColumnVisible: boolean;
	placedSymbolSelected: string;
	scrollToWarningField: {
		entryIdForOpen: string;
		scrollToFieldId: string;
	};
	settings: {
		appLoaded: boolean;
		userLoaded: boolean;
		generalSettings: Dictionary<SettingRow>;
		journalSettings: Dictionary<SettingRow>;
	};
	corruptedEntriesConsent: string[];
	selectedAuthorIds: string[];
	selectedTemplateIds: string[];
	searchKeyword: string;
	selectedDateRange: { from: string, to: string };
	visibleEntryIds: string[];
	overviewEntriesLimit: number;
	foldedAll: boolean;
	extendedEntryTypes: string[];
	unfoldContent: boolean;
	foldContentLinesNumber: number;
	jasminValidationByTemplateId: Record<string, JasminValidationStatus>;
}

export const initialState: JournalState = {
	init: false,
	config: null,
	complexFieldsEnabled: environment.name !== EnvsEnum.prod && environment.name !== EnvsEnum.preprod,
	newGuid: null,
	rawEntries: null,
	rawTemplateFields: [],
	rawUserTemplates: null,
	citizenId: null,
	citizen: null,
	display: null,
	medicineEntries: null,
	editTemplate: {
		template: null,
		fields: null,
		layout: null,
		valid: false,
		mode: null,
		openForEdit: null,
		lastChangedField: null,
		fieldsToSave: null,
		templateToSave: null,
		saved: false,
		layoutVisible: true,
		goBackAfterSave: false,
		saving: false,
		clinicProfessions: []
	},
	byList: {
		sortBy: null,
		direction: null,
		printMode: false,
		entriesPerPage: null
	},
	editEntrySaved: {
		saving: false,
		entry: null,
		entryId: null,
		backToList: false,
	},
	byDrawings: {
		descriptions: false,
		bigSize: false,
		showInactiveSymbols: false,
		hiddenSchemaIds: []
	},
	drawingsColumnVisible: true,
	entriesColumnVisible: true,
	placedSymbolSelected: null,
	scrollToWarningField: {
		entryIdForOpen: null,
		scrollToFieldId: null
	},
	settings: {
		appLoaded: false,
		userLoaded: false,
		generalSettings: null,
		journalSettings: null
	},
	corruptedEntriesConsent: [],
	selectedAuthorIds: [],
	selectedTemplateIds: [],
	searchKeyword: null,
	selectedDateRange: { from: null, to: null },
	visibleEntryIds: [] as string[],
	overviewEntriesLimit: null,
	foldedAll: null,
	extendedEntryTypes: [],
	unfoldContent: true,
	foldContentLinesNumber: null,
	jasminValidationByTemplateId: {}
};

export const journalReducer = createReducer<JournalState>(
	initialState,
	on(JournalActions.initJournal, (state) => ({
		...state,
		init: true
	})),
	on(JournalActions.destroyJournal, (state) => ({
		...initialState,
		config: state.config || initialState.config,
		jasminValidationByTemplateId: state.jasminValidationByTemplateId
	})),
	on(JournalActions.newGuid, (state, { guid }) => ({ ...state, newGuid: guid })),
	on(JournalActions.setCitizenId, (state, { id }) => ({
		...state,
		citizenId: id
	})),
	on(JournalActions.citizenLoaded, (state, { citizen }) => ({
		...state,
		citizen
	})),
	on(JournalActions.getConfig, (state, { config }) => ({
		...state,
		config
	})),
	on(JournalActions.entriesLoaded, ((state, { entries }) => ({
		...state,
		rawEntries: entries
	}))),
	on(TemplateActions.templateFieldsLoaded, ((state, { templateFields }) => ({
		...state,
		rawTemplateFields: [...state.rawTemplateFields, ...templateFields]
	}))),
	on(TemplateActions.singleTemplateFieldLoaded, ((state, { templateField }) => ({
		...state,
		rawTemplateFields: [...state.rawTemplateFields, templateField]
	}))),
	on(JournalActions.saveEntryForEdit, ((state) => ({
		...state,
		editEntrySaved: { ...initialState.editEntrySaved, saving: true },
		medicineEntries: []
	}))),
	on(JournalActions.entryForEditSaved, ((state, { entry, backToList, entryId }) => ({
		...state,
		editEntrySaved: { entry, backToList, entryId, saving: false }
	}))),
	on(JournalActions.clearEntryForEditSaved, (state) => ({
		...state,
		editEntrySaved: { ...initialState.editEntrySaved }
	})),
	on(JournalActions.navigateToView, (state, { view }) => ({
		...state,
		view
	})),
	on(JournalActions.sortListBy, (state, { by, direction }) => ({
		...state,
		byList: { ...state.byList, sortBy: by, direction }
	})),
	on(JournalActions.togglePrintModeByList, (state, { toggle }) => ({
		...state,
		byList: { ...state.byList, printMode: toggle }
	})),
	on(JournalActions.entriesPerPagedByList, (state, { entriesPerPage }) => ({
		...state,
		byList: { ...state.byList, entriesPerPage }
	})),
	on(TemplateActions.userTemplatesLoaded, (state, { templates }) => ({
		...state,
		rawUserTemplates: templates
	})),
	on(TemplateActions.validateJasminTemplate, (state, { template }) => ({
		...state,
		jasminValidationByTemplateId: {
			...state.jasminValidationByTemplateId,
			[template.templateId]: 'validating' as JasminValidationStatus
		}
	})),
	on(TemplateActions.jasminTemplateValidationSucceeded, (state, { templateId }) => ({
		...state,
		jasminValidationByTemplateId: {
			...state.jasminValidationByTemplateId,
			[templateId]: 'valid' as JasminValidationStatus
		}
	})),
	on(TemplateActions.jasminTemplateValidationFailed, (state, { templateId }) => ({
		...state,
		jasminValidationByTemplateId: {
			...state.jasminValidationByTemplateId,
			[templateId]: 'failed' as JasminValidationStatus
		}
	})),
	on(TemplateActions.jasminTemplateValidationSkipped, (state, { templateId }) => ({
		...state,
		jasminValidationByTemplateId: {
			...state.jasminValidationByTemplateId,
			[templateId]: 'idle' as JasminValidationStatus
		}
	})),
	on(TemplateActions.convertTemplates, (state, { templates, entryTypes }) => ({
		...state,
		allTemplates: templates.map(item => enhanceWithEntryType(item, entryTypes))
	})),

	on(TemplateActions.resetTemplates, (state) => ({
		...state,
		rawUserTemplates: null,
	})),
	on(TemplateActions.prepTemplateForCreate, (state) => ({
		...state,
		editTemplate: {
			...initialState.editTemplate,
			template: {
				fields: [],
				createDate: new Date().toISOString(),
				active: false
			},
			valid: false,
			saving: false,
			mode: JournalEditMode.create,
			fields: {},
			layout: { rows: [[]] },
			clinicProfessions: state.editTemplate.clinicProfessions
		}
	})),
	on(TemplateActions.prepTemplateForEdit, (state) => ({
		...state,
		editTemplate: {
			...initialState.editTemplate,
			mode: JournalEditMode.edit,
			saving: false,
			clinicProfessions: state.editTemplate.clinicProfessions
		}
	})),
	on(TemplateActions.prepTemplateForDuplicate, (state) => ({
		...state,
		editTemplate: {
			...initialState.editTemplate,
			mode: JournalEditMode.duplicate,
			saving: false,
			clinicProfessions: state.editTemplate.clinicProfessions
		}
	})),
	on(TemplateActions.templateForEditLoaded, (state, {
		template,
		fields,
		layout
	}) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			template,
			fields,
			layout,
			saved: false,
			saving: false
		}
	})),
	on(TemplateActions.trySubmitTemplate, (state, { template }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			template: {
				...state.editTemplate.template,
				...template
			},
			saving: true
		}
	})),
	on(TemplateActions.upsertTemplate, (state) => ({
		...state,
		editTemplate: { ...state.editTemplate, saved: false }
	})),
	on(TemplateActions.templateSaved, (state) => ({
		...state,
		editTemplate: { ...state.editTemplate, saved: true }
	})),
	on(JournalActions.medicineEntriesLoaded, (state, { entries }) => ({
		...state,
		medicineEntries: entries
	})),
	on(TemplateActions.destroyEditTemplate, state => ({
		...state,
		editTemplate: {
			...initialState.editTemplate,
			clinicProfessions: state.editTemplate.clinicProfessions
		}
	})),
	on(TemplateActions.editTemplateField, (state, { templateFieldId }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			openForEdit: templateFieldId
		}
	})),
	on(TemplateActions.updateTemplateName, (state, { name }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			template: {
				...state.editTemplate.template,
				name
			}
		}
	})),
	on(TemplateActions.updateTemplateProfessions, (state, { professions }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			template: {
				...state.editTemplate.template,
				professions
			}
		}
	})),
	on(TemplateActions.orderTemplateFields, (state, { fields }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			template: {
				...state.editTemplate.template,
				fields
			},
			fields: fields.reduce((acc, k) => {
				const field = state.editTemplate.fields[k];
				return { ...acc, ...{ [k]: { ...field } } };
			}, {})
		}
	})),
	on(TemplateActions.updateRowLayout, (state, { row, index }) => {
		const newRows = [...state.editTemplate.layout.rows];
		newRows[index] = [...row];
		const lastChangedField = row.map(({ templateFieldId }) => templateFieldId);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField,
				layout: {
					...state.editTemplate.layout,
					rows: newRows
				}
			}
		};
	}),
	on(TemplateActions.updateRowOrder, (state, { from, to }) => {
		const rows = [...state.editTemplate.layout.rows];
		const elem = rows[from];
		rows.splice(from, 1);
		rows.splice(to, 0, elem);
		const lastChangedField = elem.map(({ templateFieldId }) => templateFieldId);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField,
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.orderOrAddTemplateFields, (state, { fields }) => {
		const newFields = fields.reduce((acc, tf) => {
			const field = state.editTemplate.fields[tf.templateFieldId] || { field: tf, valid: true, touched: false };
			return { ...acc, ...{ [tf.templateFieldId]: { ...field } } };
		}, {});
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				template: {
					...state.editTemplate.template,
					fields: fields.map(({ templateFieldId }) => templateFieldId)
				},
				fields: newFields,
				layout: {
					...state.editTemplate.layout,
					rows: fillLayout(addFieldsToLayout(fields, state.editTemplate.layout), newFields)
				}
			}
		};
	}),
	on(TemplateActions.resetInLayout, (state) => {
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField: null,
				layout: {
					...state.editTemplate.layout
				}
			}
		};
	}),
	on(TemplateActions.moveTemplateFieldInLayout, (state, { coords }) => {
		const lastChangedField = [state.editTemplate.layout.rows[coords.startRow][coords.startColumn].templateFieldId];
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField,
				layout: {
					...state.editTemplate.layout,
					rows: fillLayout(vectorMoveField(state.editTemplate.layout.rows, coords), state.editTemplate.fields)
				}
			}
		};
	}),
	on(TemplateActions.moveTemplateFieldToBeginningInLayout, (state, { coords }) => {
		const lastChangedField = [state.editTemplate.layout.rows[coords.startRow][coords.startColumn].templateFieldId];
		const fields = state.editTemplate.fields;
		const rows = fillLayout(vectorMoveFieldTo(state.editTemplate.layout.rows, coords), fields);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField,
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.moveTemplateFieldToEndInLayout, (state, { coords }) => {
		const lastChangedField = [state.editTemplate.layout.rows[coords.startRow][coords.startColumn].templateFieldId];
		const fields = state.editTemplate.fields;
		const rows = fillLayout(vectorMoveFieldTo(state.editTemplate.layout.rows, coords, false), fields);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField,
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.moveNewTemplateRowInLayoutBetween, (state, { item, row, fromRow }) => {
		const layoutRows = [...state.editTemplate.layout.rows];
		const currentColIndex = layoutRows.map(layoutRow =>
			layoutRow.findIndex(r => r.templateFieldId === item.templateFieldId)).find(r => r !== -1);
		const currentRowIndex = fromRow >= row ? (fromRow + 1) : fromRow;

		layoutRows.splice(row, 0, [{ size: 12, templateFieldId: item.templateFieldId }]);
		const layoutColumns = [...layoutRows[currentRowIndex]];
		layoutColumns.splice(currentColIndex, 1);
		let newRows = [...layoutRows];
		newRows.splice(currentRowIndex, 1, layoutColumns);

		if (((row === fromRow) || (--row === fromRow)) && layoutRows[currentRowIndex].length === 1) {
			newRows = [...state.editTemplate.layout.rows];
		} else {
			const { fields } = state.editTemplate;
			const restoredFields = restoreFields(newRows[currentRowIndex], fields);
			if (!isEmpty(restoredFields)) {
				newRows[currentRowIndex] = restoreRow(newRows[currentRowIndex], restoredFields);
			}
		}

		const rows = [...newRows.filter(r => r.length)];

		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				lastChangedField: [item.templateFieldId],
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.addNewTemplateFieldInLayout, (state, { item, column, row }) => {
		const valid = isComplexOrWidgetFn(item.fieldType);
		const fields = {
			...state.editTemplate.fields,
			[item.templateFieldId]: { field: item, touched: false, valid }
		};
		const rows = fillLayout(addFieldInLayout(
			state.editTemplate.layout.rows,
			{ column, row },
			{ templateFieldId: item.templateFieldId, size: item.minSize }
		), fields);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				template: {
					...state.editTemplate.template,
					fields: [...state.editTemplate.template.fields, item.templateFieldId]
				},
				lastChangedField: [item.templateFieldId],
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.addNewTemplateRowInLayoutBetween, (state, { item, row }) => {
		const valid = isComplexOrWidgetFn(item.fieldType);
		const fields = {
			...state.editTemplate.fields,
			[item.templateFieldId]: { field: item, touched: false, valid }
		};
		const rowsClone = [...state.editTemplate.layout.rows];
		rowsClone.splice(row, 0, [{ size: 12, templateFieldId: item.templateFieldId }]);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				template: {
					...state.editTemplate.template,
					fields: [...state.editTemplate.template.fields, item.templateFieldId]
				},
				lastChangedField: [item.templateFieldId],
				layout: {
					...state.editTemplate.layout,
					rows: rowsClone
				}
			}
		};
	}),
	on(TemplateActions.addNewFirstTemplateFieldInLayout, (state, { item }) => {
		const valid = isComplexOrWidgetFn(item.fieldType);
		const fields = {
			...state.editTemplate.fields,
			[item.templateFieldId]: { field: item, touched: false, valid }
		};
		const rows = addFieldInLayoutAtStartOrEnd(state.editTemplate.layout.rows, { templateFieldId: item.templateFieldId, size: item.minSize })
			.filter(row => row.length);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				lastChangedField: [item.templateFieldId],
				template: {
					...state.editTemplate.template,
					fields: [item.templateFieldId, ...state.editTemplate.template.fields]
				},
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.addNewLastTemplateFieldInLayout, (state, { item }) => {
		const valid = isComplexOrWidgetFn(item.fieldType);
		const fields = {
			...state.editTemplate.fields,
			[item.templateFieldId]: { field: item, touched: false, valid }
		};
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				lastChangedField: [item.templateFieldId],
				template: {
					...state.editTemplate.template,
					fields: [item.templateFieldId, ...state.editTemplate.template.fields]
				},
				layout: {
					...state.editTemplate.layout,
					rows: addFieldInLayoutAtStartOrEnd(
						state.editTemplate.layout.rows,
						{ templateFieldId: item.templateFieldId, size: item.minSize },
						false
					)
				}
			}
		};
	}),
	on(TemplateActions.addSchema, (state, { item }) => {
		const fields = {
			...state.editTemplate.fields,
			[item.templateFieldId]: { field: item, touched: false, valid: true }
		};
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				lastChangedField: [item.templateFieldId],
				template: {
					...state.editTemplate.template,
					fields: [item.templateFieldId, ...state.editTemplate.template.fields]
				}
			}
		};
	}),
	on(TemplateActions.deleteSchema, (state, { item }) => {
		const { [item.templateFieldId]: pluck, ...fields } = {
			...state.editTemplate.fields
		};
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				lastChangedField: [item.templateFieldId],
				template: {
					...state.editTemplate.template,
					fields: [...state.editTemplate.template.fields].filter(f => f !== item.templateFieldId)
				}
			}
		};
	}),
	on(TemplateActions.updateTemplateForEdit, (state, { template, valid }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			template,
			valid,
			saved: false
		}
	})),
	on(TemplateActions.updateTemplateField, (state, { templateField }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			saved: false,
			openForEdit: null,
			lastChangedField: [templateField.field.templateFieldId],
			fields: {
				...state.editTemplate.fields,
				[templateField.field.templateFieldId]: templateField
			}
		}
	})),
	on(TemplateActions.deleteTemplateField, (state, { templateFieldId }) => {
		const { [templateFieldId]: rem, ...fields } = state.editTemplate.fields;
		const fieldsList = [...state.editTemplate.template.fields].filter(f => f !== templateFieldId);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				fields,
				template: {
					...state.editTemplate.template,
					fields: fieldsList
				},
				layout: {
					...state.editTemplate.layout,
					rows: fillLayout(
						removeFieldInLayoutAt(
							state.editTemplate.layout.rows,
							getPositionInLayoutAsObject(state.editTemplate.layout.rows, templateFieldId)
						), fields
					),
				}
			}
		};
	}),
	on(TemplateActions.deprecateTemplateField, (state, { templateFieldId }) => {
		const { column, row } = getPositionInLayoutAsObject(state.editTemplate.layout.rows, templateFieldId);
		const fields = state.editTemplate.fields;
		const rows = fillLayout(vectorMoveFieldTo(state.editTemplate.layout.rows, { startColumn: column, startRow: row }, false), fields);
		return {
			...state,
			editTemplate: {
				...state.editTemplate,
				saved: false,
				openForEdit: null,
				lastChangedField: [templateFieldId],
				fields: {
					...state.editTemplate.fields,
					[templateFieldId]: {
						...state.editTemplate.fields[templateFieldId],
						field: {
							...state.editTemplate.fields[templateFieldId].field,
							deprecated: true
						}
					}
				},
				layout: {
					...state.editTemplate.layout,
					rows
				}
			}
		};
	}),
	on(TemplateActions.cancelTemplateFieldEdit, state => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			openForEdit: null
		}
	})),
	on(TemplateActions.toggleTemplateLayout, state => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			layoutVisible: !state.editTemplate.layoutVisible
		}
	})),
	on(TemplateActions.toggleTemplateGoBackAfterSave, (state, { goBackAfterSave }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			goBackAfterSave
		}
	})),
	on(JournalActions.toggleEntryInOverview, (state, { entryId, fieldId }) => ({
		...state,
		scrollToWarningField: {
			entryIdForOpen: entryId,
			scrollToFieldId: fieldId
		}
	})),
	on(JournalActions.toggleDrawingsBigSize, state => ({
		...state,
		byDrawings: {
			...state.byDrawings,
			bigSize: !state.byDrawings.bigSize
		}
	})),
	on(JournalActions.toggleDrawingsDescriptions, state => ({
		...state,
		byDrawings: {
			...state.byDrawings,
			descriptions: !state.byDrawings.descriptions
		}
	})),
	on(JournalActions.toggleEntriesColumnVisibility, (state) => {
		const entriesColumnVisible = state.drawingsColumnVisible ? !state.entriesColumnVisible : true;
		return {
			...state,
			entriesColumnVisible
		};
	}),
	on(JournalActions.toggleDrawingsColumnVisibility, (state) => {
		const drawingsColumnVisible = state.entriesColumnVisible ? !state.drawingsColumnVisible : true;
		return {
			...state,
			drawingsColumnVisible
		};
	}),
	on(JournalActions.selectPlacedSymbolId, (state, { placedSymbolId }) => ({
		...state,
		placedSymbolSelected: placedSymbolId
	})),
	on(JournalActions.toggleInactivePlacedSymbols, (state) => ({
		...state,
		byDrawings: {
			...state.byDrawings,
			showInactiveSymbols: !state.byDrawings.showInactiveSymbols
		}
	})),
	on(JournalActions.resetSelectPlacedSymbolId, (state) => ({
		...state,
		placedSymbolSelected: null
	})),
	on(JournalActions.setHiddenSchemaIds, (state, { schemaIds }) => ({
		...state,
		byDrawings: {
			...state.byDrawings,
			hiddenSchemaIds: schemaIds
		}
	})),
	on(TemplateActions.loadClinicProfessionsSuccess, (state, { professions }) => ({
		...state,
		editTemplate: {
			...state.editTemplate,
			clinicProfessions: professions
		}
	})),
	on(JournalActions.changeOverviewEntriesLimit, (state, { limit }) => ({
		...state,
		overviewEntriesLimit: limit
	})),
	on(JournalActions.loadAppSettings, (state, { settings }) => {
		const s = settings || [];
		const generalSettings = s.reduce((acc, cv) => {
			return ({
				...acc, [cv.key]: {
					...cv,
					value: tryParseJson(cv.value as string)
				}
			});
		}, {});
		return {
			...state,
			settings: {
				...state.settings,
				appLoaded: true,
				generalSettings
			}
		};
	}),
	on(JournalActions.loadUserSettings, (state, { settings }) => {
		const s = settings || [];
		const journalSettings = s.reduce((acc, cv) => {
			const value: string = tryParseJson(cv.value as string);
			return ({
				...acc, [cv.key]: {
					...cv,
					value: tryParseJson(value)
				}
			});
		}, {});
		return {
			...state,
			settings: {
				...state.settings,
				userLoaded: true,
				journalSettings
			}
		};
	}),
	on(JournalActions.applySettings, (state, { settings }) => {
		const parseEntryTypes = (value: unknown): string[] => {
			if (typeof value === 'string') {
				return tryParseJson(value) || [];
			}
			return Array.isArray(value) ? value : [];
		};

		const foldedAll = parseBoolean(getSettingValue(settings, JournalSettingsKeys.foldedAll));
		const entriesPerPage = Number(getSettingValue(settings, JournalSettingsKeys['list.entriesPerPage'])) || 0;
		const overviewEntriesLimit = Number(getSettingValue(settings, JournalSettingsKeys['overview.entriesLimit'])) ||
			getDefaultValue(JournalSettingsKeys['overview.entriesLimit']) as number;
		const entryTypes = parseEntryTypes(getSettingValue(settings, JournalSettingsKeys.entryTypeExtended));
		const unfoldContent = parseBoolean(getSettingValue(settings, JournalSettingsKeys.unfoldContent)) ??
			getDefaultValue(JournalSettingsKeys.unfoldContent) as boolean;
		const foldContentLinesNumber = Number(getSettingValue(settings, JournalSettingsKeys.foldContentLinesNumber)) ||
			getDefaultValue(JournalSettingsKeys.foldContentLinesNumber) as number;

		return {
			...state,
			byList: { ...state.byList, entriesPerPage },
			overviewEntriesLimit,
			foldedAll,
			extendedEntryTypes: entryTypes,
			unfoldContent,
			foldContentLinesNumber
		};
	}),
	on(JournalActions.fillWithDefaults, (state, { settings }) => {
		const current = state.settings.journalSettings || {};
		const filtered = settings.filter(s => !current.hasOwnProperty(s.key));
		const missingDefaultSettings = filtered.reduce((acc, cv) => {
			return ({ ...acc, [cv.key]: { ...cv } });
		}, {});
		return {
			...state,
			settings: {
				...state.settings,
				userLoaded: true,
				journalSettings: { ...state.settings.journalSettings, ...missingDefaultSettings }
			}
		};
	}),
	on(JournalActions.saveSingleKey, (state, { key, value }) => {
		const settingRow = { ...state.settings.journalSettings[key] };
		return {
			...state,
			settings: {
				...state.settings,
				journalSettings: {
					...state.settings.journalSettings,
					[key]: {
						...settingRow,
						key,
						value
					}
				}
			}
		};
	}),
	on(JournalActions.submitConsentForCorruptedEntry, (state, { entryId }) => ({
		...state,
		corruptedEntriesConsent: [...state.corruptedEntriesConsent, entryId]
	})),
	on(JournalActions.filterEntriesByAuthors, (state, { authorIds }) => ({
		...state,
		selectedAuthorIds: authorIds
	})),
	on(JournalActions.filterEntriesByTemplates, (state, { templateIds }) => ({
		...state,
		selectedTemplateIds: templateIds
	})),
	on(JournalActions.filterEntriesByKeywords, (state, { keyword }) => ({
		...state,
		searchKeyword: keyword
	})),
	on(JournalActions.filterEntriesByDateRange, (state, { from, to }) => ({
		...state,
		selectedDateRange: { from, to }
	})),
	on(JournalActions.expandAllEntries, (state) => ({
		...state,
		visibleEntryIds: state.rawEntries.map(e => e.entryId),
		foldedAll: false
	})),
	on(JournalActions.foldAllEntries, (state) => ({
		...state,
		visibleEntryIds: [] as string[],
		foldedAll: true
	})),
	on(JournalActions.toggleEntryVisibility, (state, { entryId }) => ({
		...state,
		visibleEntryIds: state.visibleEntryIds.includes(entryId)
			? state.visibleEntryIds.filter(id => id !== entryId)
			: [...state.visibleEntryIds, entryId],
	})),
	on(JournalActions.setVisibleEntryIds, (state, { entryIds }) => ({
		...state,
		visibleEntryIds: [...state.visibleEntryIds, ...entryIds]
	}))
);
